import { redirectToLogin } from '@/lib/identity'
import { renovarAccessToken } from './renovacion'
import { tokenStore } from './tokenStore'

/**
 * MÓDULO CANÓNICO DE SESIÓN (3/3) — el envoltorio de operación y la política.
 *
 * Contrato del ecosistema: `docs-ecosistema` → modelo-de-auth, «Ciclo de vida
 * del token en las webs». Fichero y API IDÉNTICOS en todas las webs SSO
 * (espejo de `tickets-web/src/auth/sesion.ts`); lo único propio de cada repo es
 * el cuerpo de `cerrarSesionLocal`.
 */

/**
 * La sesión murió DE VERDAD (el servidor rechazó la renovación). Cuando esto
 * sale de aquí, la sesión local YA se cerró y la navegación al login ya se
 * disparó.
 */
export class ApiUnauthorizedError extends Error {}

/**
 * NO se pudo renovar por RED (timeout, 5xx, sin respuesta) tras agotar los
 * reintentos. La sesión sigue INTACTA: el usuario reintenta su acción. El
 * mensaje es apto para la UI; el detalle técnico va aparte.
 */
export class ErrorDeRed extends Error {
  readonly detalle: string
  constructor(detalle: string) {
    super('No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.')
    this.detalle = detalle
  }
}

/**
 * La renovación FUNCIONÓ (hay token nuevo) y aun así la operación volvió a
 * responder «no autenticado». La sesión sigue INTACTA, y no por benevolencia:
 * el gateway acaba de renovarla, que es la prueba más fuerte que existe de que
 * está viva. Lo que falla es ese servicio con ese token.
 *
 * Antes esto cerraba la sesión, y era el peor error posible de los dos que se
 * pueden cometer aquí: se expulsa a alguien cuya sesión funciona, hacia un
 * login que no arregla nada, y de paso se le tira la sesión en las demás webs
 * del ecosistema (la cookie del refresh es compartida en `.tecopos.com`).
 * Contrato: `docs-ecosistema` → modelo-de-auth, «El segundo 401 NO cierra la
 * sesión» (Víctor, 2026-08-25).
 */
export class OperacionRechazada extends Error {
  readonly detalle: string
  constructor(detalle: string) {
    super(
      'El servidor rechazó la petición aunque tu sesión sigue activa. Vuelve a intentarlo; si sigue igual, avisa a soporte.',
    )
    this.detalle = detalle
  }
}

/**
 * Señal INTERNA que una operación lanza cuando el backend rechazó por sesión
 * (HTTP 401 en REST, o `extensions.code === 'UNAUTHENTICATED'` en el gateway).
 * `FORBIDDEN`/`PERMISSION_DENIED` **nunca** es sesión (raíz §5.6): es
 * autorización y reloguearse no la arregla. No sale de esta capa:
 * `ejecutarConSesion` la traduce a renovación+reintento o a
 * `ApiUnauthorizedError`.
 */
export class SesionInvalida extends Error {}

/**
 * ÚNICO punto donde muere la sesión local. **Esta función es la pieza que
 * cambia por repo** (el resto del módulo es idéntico en todas las webs):
 * - web PRIVADA como esta: limpia el token y navega a `/login`.
 * - web PÚBLICA (comunidad): degrada a visitante y abre su gate de login.
 * `tickets-web` solo limpia — su `ProtectedRoute` re-renderiza y deriva al
 * login SSO.
 */
export function cerrarSesionLocal(): void {
  tokenStore.clear()
  try {
    redirectToLogin()
  } catch {
    // `redirectToLogin` lanza A PROPÓSITO (devuelve `never`) para cortar el flujo
    // de su llamador. Aquí el corte lo da `ApiUnauthorizedError`, así que se
    // ignora: la navegación ya está pedida.
  }
}

/**
 * Renueva y aplica la política de los tres desenlaces:
 * - `renovado` → devuelve el token fresco (ya está en el store).
 * - `sesion-invalida` → cierra la sesión local y lanza `ApiUnauthorizedError`.
 * - `red` → lanza `ErrorDeRed` y **no toca la sesión**.
 */
export async function renovarOCerrarSesion(): Promise<string> {
  const desenlace = await renovarAccessToken()
  if (desenlace.tipo === 'renovado') return desenlace.token
  if (desenlace.tipo === 'sesion-invalida') {
    cerrarSesionLocal()
    throw new ApiUnauthorizedError('Sesión expirada')
  }
  throw new ErrorDeRed(desenlace.motivo)
}

/**
 * Envuelve una operación autenticada con el ciclo de vida del token (raíz
 * §5.6): la corre con el token vigente; si señala fallo de sesión, renueva
 * (single-flight, con reintentos si el problema es la red) y la reintenta UNA
 * vez con el token nuevo — todo IMPERCEPTIBLE para el usuario. El bucle de
 * reintentos vive en la RENOVACIÓN, no en la operación: si el reintento vuelve
 * a fallar por sesión, se cierra y no hay tercer intento.
 *
 * `op` recibe el token que debe usar en la cabecera (no lo relee del store):
 * así el reintento va con el token FRESCO, sin condiciones de carrera.
 *
 * Las operaciones GraphQL de esta web NO pasan por aquí: su equivalente es el
 * `errorLink` de `@/lib/apollo/client`, que aplica la MISMA política con
 * `renovarOCerrarSesion`. Este envoltorio es para el REST propio del
 * portal-server (clientes de `features/*`, cuya adopción es trabajo posterior).
 */
export async function ejecutarConSesion<T>(
  op: (token: string | null) => Promise<T>,
): Promise<T> {
  const vigente = tokenStore.get()
  // Sin token no se manda la petición: iría con `Bearer ` vacío, se sabe de
  // antemano que el servidor la rechaza, y su 401 llegaría **después** de que
  // la renovación en curso se haya resuelto — abriendo una SEGUNDA renovación
  // que rota la cookie otra vez. Se entra directo en la cola del single-flight.
  if (vigente === null) return reintentarConTokenFresco(op)
  try {
    return await op(vigente)
  } catch (e) {
    if (!(e instanceof SesionInvalida)) throw e
    return reintentarConTokenFresco(op)
  }
}

/**
 * Cuánto tiempo se recuerda que un token recién emitido fue rechazado. No
 * cerrar la sesión ante el segundo 401 tiene un coste que hay que pagar aquí:
 * si el rechazo es PERSISTENTE, cada acción del usuario dispararía su propia
 * renovación, y **cada renovación rota la cookie del refresh**. Dentro de esta
 * ventana, y solo mientras el token vigente siga siendo el mismo que ya
 * rechazaron, se responde sin volver a renovar.
 */
export const VENTANA_TOKEN_RECHAZADO_MS = 30_000

let tokenRechazado: { token: string; en: number } | null = null

/** Solo para los tests: el módulo guarda estado entre operaciones. */
export function olvidarTokenRechazado(): void {
  tokenRechazado = null
}

const MOTIVO_RECHAZO =
  'la operación siguió respondiendo 401/UNAUTHENTICATED con un access token recién renovado'

async function reintentarConTokenFresco<T>(
  op: (token: string | null) => Promise<T>,
): Promise<T> {
  const vigente = tokenStore.get()
  if (
    vigente !== null &&
    tokenRechazado?.token === vigente &&
    Date.now() - tokenRechazado.en < VENTANA_TOKEN_RECHAZADO_MS
  ) {
    // Este mismo token ya se estrelló hace nada: renovar otra vez rotaría la
    // cookie para volver al mismo sitio.
    throw new OperacionRechazada(MOTIVO_RECHAZO)
  }
  // Lanza `ApiUnauthorizedError` (sesión muerta, ya cerrada) o `ErrorDeRed`
  // (sesión intacta) — en ninguno de los dos casos se reintenta la operación.
  const nuevo = await renovarOCerrarSesion()
  try {
    const r = await op(nuevo)
    // Funcionó: lo que hubiera apuntado deja de valer.
    tokenRechazado = null
    return r
  } catch (e) {
    // Segundo rechazo CON un token recién emitido: no se toca la sesión (ver
    // `OperacionRechazada`). No hay tercer intento — el bucle de reintentos
    // vive en la renovación, y renovar otra vez no cambiaría nada.
    if (e instanceof SesionInvalida) {
      tokenRechazado = { token: nuevo, en: Date.now() }
      throw new OperacionRechazada(MOTIVO_RECHAZO)
    }
    throw e
  }
}
